import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from '../config/api';
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import {
  ArrowLeft, Check, Loader2, Shield, AlertTriangle,
  FileText, Search, RefreshCw
} from "lucide-react";
import { InfoTooltip } from "../components/InfoTooltip";

import { LOGO_URL } from "../config/constants";

/**
 * Standalone Risk Assessment page (Mid-Point review + Catch-Up).
 *
 * 30 Apr 2026 — answer-driven scoring (System A canonical):
 *  - Pre-RA gate question: *Is there already a risk assessment on file?*
 *    Yes → matter reference lookup → prefill from System A record.
 *    No  → full RA flow with all 8 answers required.
 *  - 8 L/M/H factor radios; computed band fetched from /risk/calculate
 *    and rendered read-only — no client-side band calculator, no
 *    free-pick override select.
 *  - Narrative + mitigations required only when computed band != Low.
 *  - Override section visible only to supervisors / admins (server-side
 *    /risk/check-supervisor decides). Fee earner role hides it.
 */

const RISK_CATEGORIES = [
  { value: "low", label: "Low", color: "bg-status-green-dim text-status-green border-green-200" },
  { value: "medium", label: "Medium", color: "bg-status-amber-dim text-status-amber border-amber-200" },
  { value: "high", label: "High", color: "bg-status-red-dim text-status-red border-red-200" }
];

// 8 factors — wording mirrors hub/risk_assessment.py RISK_FACTORS so a
// future spec drift on the BE is surfaced by the smoke tests rather than
// hidden behind a different label.
const RISK_FACTORS = [
  { id: "complexity",          label: "Complexity of matter",      description: "Legal complexity, number of parties, unusual provisions" },
  { id: "value",               label: "Value at stake",            description: "Financial exposure relative to fee income" },
  { id: "client_vulnerability",label: "Client vulnerability",      description: "Consumer client, language barriers, unrepresented parties" },
  { id: "regulatory",          label: "Regulatory risk",           description: "AML concerns, sanctions, PEP status (weighted)" , weighted: true },
  { id: "conflict_risk",       label: "Conflict of interest risk", description: "Multiple clients, former-client relationships (weighted)", weighted: true },
  { id: "time_pressure",       label: "Time pressure",             description: "Tight deadlines, penalty clauses, exchange dates" },
  { id: "insurance",           label: "Insurance / Indemnity",     description: "PII exposure, high-value undertakings (weighted)", weighted: true },
  { id: "funding",             label: "Funding / Payment risk",    description: "Source-of-funds concerns, delayed payments" },
];

// BM-H3324 (30 Sep 2026): AML Policy §6.2, a rationale on every risk
// assessment (Vanessa: "rationale on all RAs"). The narrative is required at
// every band; mitigating actions at Medium and High. Min 10 chars matches the
// BE validator (hub.risk_assessment.NARRATIVE_MIN_CHARS).
const NARRATIVE_MIN_CHARS = 10;

const OVERRIDE_REASON_MIN_CHARS = 20;

function getCurrentUserEmail() {
  try {
    const u = localStorage.getItem("user");
    if (u) {
      const parsed = JSON.parse(u);
      if (parsed?.email) return parsed.email;
    }
  } catch (e) {}
  return (
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    ""
  );
}

export default function RiskAssessment() {
  const navigate = useNavigate();

  // Flow state.
  // Steps: "gate" (RA on file?) → "lookup" (Yes) | "mode" (No)
  //        → "form" (full assessment).
  const [step, setStep] = useState("gate");
  const [gateAnswer, setGateAnswer] = useState(null); // "yes" | "no" | null
  const [lookupRef, setLookupRef] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const [mode, setMode] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [matters, setMatters] = useState([]);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Supervisor-only role gate for the override block.
  const currentUserEmail = useMemo(() => getCurrentUserEmail(), []);
  const [isSupervisor, setIsSupervisor] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!currentUserEmail) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/risk/check-supervisor/${encodeURIComponent(currentUserEmail)}`);
        if (!cancelled) setIsSupervisor(!!r.data?.is_supervisor);
      } catch (e) {
        if (!cancelled) setIsSupervisor(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUserEmail]);

  const [formData, setFormData] = useState({
    client_name: "",
    matter_title: "",
    matter_reference: "",
    fee_earner: "",
    matter_type: "",
    assessment_type: "",
    linked_submission_id: "",
    risk_factors: {},
    risk_narrative: "",
    mitigating_actions: "",
    material_risks: "",
    key_deadline: "",
    supervisor_notes: "",
    review_date: "",
  });

  // Server-computed band (READ-ONLY) — never set by the user.
  const [computedBand, setComputedBand] = useState(null);
  const [computedScore, setComputedScore] = useState(null);
  const [computedTriggers, setComputedTriggers] = useState([]);

  // Override (supervisor only).
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideBand, setOverrideBand] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const setRiskFactor = (id, value) => setFormData(prev => ({
    ...prev,
    risk_factors: { ...prev.risk_factors, [id]: value }
  }));

  // ── Live answer-driven band ───────────────────────────────────────
  // We never compute the band client-side — the server is the only
  // authority for the weighted-factor scoring (Reg/Conflict/Insurance
  // score 1/3/4 not 1/2/3). When all 8 answers are filled, hit
  // /risk/calculate; otherwise computedBand stays null and the badge
  // shows "Pending answers".
  const allAnswersFilled = useMemo(
    () => RISK_FACTORS.every((f) => !!formData.risk_factors[f.id]),
    [formData.risk_factors],
  );

  useEffect(() => {
    let cancelled = false;
    if (!allAnswersFilled) {
      setComputedBand(null);
      setComputedScore(null);
      setComputedTriggers([]);
      return;
    }
    (async () => {
      try {
        const r = await axios.post(`${API}/risk/calculate`, formData.risk_factors);
        if (cancelled) return;
        const t = r.data?.triggers || [];
        const incomplete = t.some((s) => String(s).startsWith("incomplete:"));
        setComputedBand(incomplete ? null : (r.data?.calculated_band || null));
        setComputedScore(incomplete ? null : (r.data?.total_score ?? null));
        setComputedTriggers(t);
      } catch (e) {
        if (cancelled) return;
        toast.error(`Risk calc: ${errorMessage(e, "Risk calculation failed")}`);
        setComputedBand(null);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.risk_factors, allAnswersFilled]);

  const narrativeRequired = Boolean(computedBand);                       // BM-H3324: every band
  const mitigationsRequired = computedBand === "Medium" || computedBand === "High";

  useEffect(() => {
    if (step === "form" && mode === "midpoint" && !selectedMatter) {
      fetchMatters();
    }
  }, [step, mode, selectedMatter]);

  const fetchMatters = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/submissions`);
      const matterSubmissions = response.data.filter(s =>
        s.form_type === "matter_intake" || s.form_type === "pre_caseminder"
      );
      setMatters(matterSubmissions);
    } catch (error) {
      toast.error("Failed to load matters");
    } finally {
      setLoading(false);
    }
  };

  const loadMatterData = async (matter) => {
    setSelectedMatter(matter);
    try {
      const response = await axios.get(`${API}/submissions/${matter.id}`);
      const data = response.data.data || {};
      set("client_name", data.Q1 || matter.primary_label || "");
      set("matter_title", data.Q3 || matter.secondary_label || "");
      set("matter_reference", data.Q4 || "");
      set("fee_earner", data.Q6 || matter.tertiary_label || "");
      set("matter_type", data.Q9 || "");
      set("linked_submission_id", matter.id);
      set("material_risks", data.Q28 || "");
      set("key_deadline", data.Q29 || "");
      toast.success(`Loaded data from: ${matter.secondary_label}`);
    } catch (error) {
      toast.error("Failed to load matter data");
    }
  };

  const filteredMatters = matters.filter(m => {
    const term = searchTerm.toLowerCase();
    return (
      (m.primary_label || "").toLowerCase().includes(term) ||
      (m.secondary_label || "").toLowerCase().includes(term)
    );
  });

  // ── Pre-RA gate ───────────────────────────────────────────────────
  const handleGateAnswer = async (answer) => {
    setGateAnswer(answer);
    if (answer === "no") {
      setStep("mode");
    } else {
      // "yes" → ask for matter reference, then look up.
      setStep("lookup");
    }
  };

  const performLookup = async () => {
    const ref = (lookupRef || "").trim();
    if (!ref) {
      toast.error("Please enter a matter reference to look up.");
      return;
    }
    setLookupLoading(true);
    try {
      const r = await axios.get(`${API}/risk/has-assessment-on-file/${encodeURIComponent(ref)}`);
      setLookupResult(r.data);
      if (r.data?.has_assessment) {
        // Prefill from the looked-up record.
        set("client_name", r.data.client_name || "");
        set("matter_title", r.data.matter_title || "");
        set("matter_reference", r.data.matter_reference || ref);
        set("fee_earner", r.data.fee_earner || "");
        set("linked_submission_id", r.data.original_submission_id || "");
        toast.success(`RA found on file (band: ${r.data.calculated_band}).`);
      } else {
        toast.message("No RA found on file — running full assessment instead.");
      }
    } catch (e) {
      toast.error("Lookup failed: " + errorMessage(e, "Lookup failed"));
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Validation ────────────────────────────────────────────────────
  const validationErrors = useMemo(() => {
    const e = {};
    if (!formData.client_name.trim()) e.client_name = "Client name is required";
    if (!formData.matter_title.trim()) e.matter_title = "Matter title is required";
    const missingFactors = RISK_FACTORS
      .filter((f) => !formData.risk_factors[f.id])
      .map((f) => f.label);
    if (missingFactors.length > 0) {
      e.risk_factors = `Answer all 8 risk factors. Missing: ${missingFactors.join(", ")}`;
    }
    if (narrativeRequired) {
      const rn = (formData.risk_narrative || "").trim();
      if (rn.length < NARRATIVE_MIN_CHARS) {
        e.risk_narrative = `Required for every risk assessment (min ${NARRATIVE_MIN_CHARS} chars).`;
      }
    }
    if (mitigationsRequired) {
      const ma = (formData.mitigating_actions || "").trim();
      if (ma.length < NARRATIVE_MIN_CHARS) {
        e.mitigating_actions = `Required for ${computedBand} band (min ${NARRATIVE_MIN_CHARS} chars).`;
      }
    }
    return e;
  }, [formData, narrativeRequired, mitigationsRequired, computedBand]);

  const hasErrors = Object.keys(validationErrors).length > 0;

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (hasErrors) {
      const first = Object.values(validationErrors)[0];
      toast.error(first);
      return;
    }
    setSubmitting(true);
    try {
      // Build the System A payload — ONLY the 8 answers + narrative
      // fields. We deliberately do NOT post a band/score: server
      // rejects client-supplied band/score with 400.
      const payload = {
        form_type: "risk_assessment",
        assessment_type: mode,
        client_name: formData.client_name,
        matter_title: formData.matter_title,
        matter_reference: formData.matter_reference,
        fee_earner: formData.fee_earner,
        linked_submission_id: formData.linked_submission_id,
        risk_factors: formData.risk_factors,
        risk_narrative: formData.risk_narrative,
        mitigating_actions: formData.mitigating_actions,
        material_risks: formData.material_risks,
        key_deadline: formData.key_deadline,
        review_date: formData.review_date,
        supervisor_notes: formData.supervisor_notes,
        assessed_at: new Date().toISOString(),
      };
      const response = await axios.post(`${API}/submissions`, { data: payload });
      toast.success("Risk assessment saved.");
      navigate(`/submission/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to save: " + errorMessage(error, "Failed to save"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverrideSave = async () => {
    if (!isSupervisor) {
      toast.error("Only supervisors can override the computed band.");
      return;
    }
    if (!overrideBand) {
      toast.error("Pick the override band.");
      return;
    }
    if (overrideReason.trim().length < OVERRIDE_REASON_MIN_CHARS) {
      toast.error(`Override reason must be at least ${OVERRIDE_REASON_MIN_CHARS} characters.`);
      return;
    }
    if (!formData.linked_submission_id) {
      toast.error("Save the assessment first, then apply an override.");
      return;
    }
    try {
      await axios.post(
        `${API}/risk/submission/${formData.linked_submission_id}/override`,
        {
          override_risk_band:
            overrideBand.charAt(0).toUpperCase() + overrideBand.slice(1),
          override_reason: overrideReason.trim(),
          override_by: currentUserEmail,
          override_by_email: currentUserEmail,
        },
      );
      toast.success("Override saved.");
      setOverrideOpen(false);
    } catch (e) {
      toast.error("Override failed: " + errorMessage(e, "Override failed"));
    }
  };

  const riskColor = (level) =>
    RISK_CATEGORIES.find((r) => r.value === (level || "").toLowerCase())?.color || "";

  // ── UI: Pre-RA gate question (Step 1 of new flow) ─────────────────
  if (step === "gate") {
    return (
      <div className="min-h-screen bg-[#F5F5F0]">
        <PageHeader navigate={navigate} subtitle="Pre-RA gate" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl font-semibold text-teal mb-2 text-center">
            Is there already a risk assessment on file for this matter?
          </h2>
          <p className="text-slate-500 text-center mb-8">
            If a CaseMinder FORF has already produced an RA, we'll prefill from it.
            If not, we'll run the full 8-factor flow.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <button
              onClick={() => handleGateAnswer("yes")}
              className="group bg-white p-8 rounded-md border-2 border-teal hover:shadow-lg transition-all text-left"
              data-testid="ra-gate-yes"
            >
              <h3 className="font-semibold text-teal text-lg mb-2">Yes — already on file</h3>
              <p className="text-sm text-slate-500">
                Look up the matter reference and prefill from the existing
                CaseMinder record.
              </p>
            </button>
            <button
              onClick={() => handleGateAnswer("no")}
              className="group bg-white p-8 rounded-md border-2 border-blue-200 hover:border-blue-500 hover:shadow-lg transition-all text-left"
              data-testid="ra-gate-no"
            >
              <h3 className="font-semibold text-blue-800 text-lg mb-2">No — need to do one</h3>
              <p className="text-sm text-slate-500">
                Run the full 8-factor risk assessment. All 8 answers are
                required; the band is computed from your answers.
              </p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── UI: Lookup (Yes branch) ───────────────────────────────────────
  if (step === "lookup") {
    return (
      <div className="min-h-screen bg-[#F5F5F0]">
        <PageHeader navigate={navigate} subtitle="RA on file — lookup" />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl font-semibold text-teal mb-2 text-center">
            Look up matter
          </h2>
          <p className="text-slate-500 text-center mb-8">
            Enter the matter reference (e.g. <code>BL.RES.001.0042</code>) to
            find the existing risk assessment.
          </p>
          <div className="bg-white border rounded-md p-6 space-y-4">
            <div>
              <Label htmlFor="ra-lookup-ref">Matter reference</Label>
              <Input
                id="ra-lookup-ref"
                value={lookupRef}
                onChange={(e) => setLookupRef(e.target.value)}
                placeholder="BL.RES.001.0042"
                data-testid="ra-lookup-ref"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={performLookup} disabled={lookupLoading} data-testid="ra-lookup-go">
                {lookupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Look up
              </Button>
              <Button variant="outline" onClick={() => setStep("gate")} data-testid="ra-lookup-back">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </div>
            {lookupResult && (
              <div className="mt-4 p-3 rounded-md border bg-slate-50 text-sm" data-testid="ra-lookup-result">
                <p className="font-medium">{lookupResult.message}</p>
                {lookupResult.has_assessment ? (
                  <>
                    <p className="text-xs text-slate-600 mt-1">
                      Calculated band: <strong>{lookupResult.calculated_band}</strong>
                      {lookupResult.override_band && (
                        <> · Override: <strong>{lookupResult.override_band}</strong></>
                      )}
                    </p>
                    <Button
                      className="mt-3"
                      onClick={() => {
                        setMode("midpoint");
                        setStep("form");
                      }}
                      data-testid="ra-lookup-proceed"
                    >
                      Proceed with mid-point review
                    </Button>
                  </>
                ) : (
                  <Button
                    className="mt-3"
                    onClick={() => {
                      setGateAnswer("no");
                      setStep("mode");
                    }}
                    data-testid="ra-lookup-fallback"
                  >
                    No record — run full assessment
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── UI: Mode selection (No branch / fallback) ───────────────────
  if (step === "mode") {
    return (
      <div className="min-h-screen bg-[#F5F5F0]">
        <PageHeader navigate={navigate} subtitle="Choose mode" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl font-semibold text-teal mb-2 text-center">Select Assessment Type</h2>
          <p className="text-slate-500 text-center mb-8">Choose how you'd like to perform the risk assessment</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <button
              onClick={() => { setMode("midpoint"); setStep("form"); }}
              className="group bg-white p-8 rounded-md border-2 border-blue-200 hover:border-blue-500 hover:shadow-lg transition-all text-left"
              data-testid="mode-midpoint"
            >
              <div className="p-3 rounded-md bg-blue-50 group-hover:bg-blue-100 transition-colors w-fit mb-4">
                <RefreshCw className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-blue-800 text-lg mb-2">Mid-Point Review</h3>
              <p className="text-sm text-slate-500">Pull in data from an existing CaseMinder or Pre-CaseMinder matter and run a fresh risk assessment</p>
            </button>
            <button
              onClick={() => { setMode("standalone"); set("assessment_type", "standalone"); setStep("form"); }}
              className="group bg-white p-8 rounded-md border-2 border-teal hover:border-teal hover:shadow-lg transition-all text-left"
              data-testid="mode-standalone"
            >
              <div className="p-3 rounded-md bg-teal-dim group-hover:bg-teal-dim transition-colors w-fit mb-4">
                <Shield className="w-6 h-6 text-teal" />
              </div>
              <h3 className="font-semibold text-teal text-lg mb-2">Standalone / Catch-Up</h3>
              <p className="text-sm text-slate-500">Fresh risk assessment for transitional or catch-up matters not yet in CaseMinder</p>
            </button>
          </div>
          <div className="mt-6 text-center">
            <Button variant="outline" onClick={() => setStep("gate")}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to gate question
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── UI: Full RA form ──────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <PageHeader
        navigate={navigate}
        subtitle={mode === "midpoint" ? "Mid-Point Review" : "Standalone / Catch-Up"}
        rightActions={
          <Button variant="outline" size="sm" onClick={() => { setStep("gate"); setMode(null); setSelectedMatter(null); }} data-testid="change-mode">
            Change Mode
          </Button>
        }
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Midpoint: Matter Selection */}
        {mode === "midpoint" && !selectedMatter && (
          <div className="bg-white rounded-md border p-6 mb-8" data-testid="matter-selector">
            <h2 className="text-lg font-semibold text-teal mb-4">Select a Matter</h2>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by client or matter name..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="matter-search"
              />
            </div>
            {loading ? (
              <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
            ) : filteredMatters.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No matters found</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredMatters.map(m => (
                  <button
                    key={m.id}
                    onClick={() => loadMatterData(m)}
                    className="w-full p-3 bg-navy-hover/60 rounded-md border hover:border-blue-300 hover:bg-blue-50 transition-all text-left flex items-center justify-between group"
                    data-testid={`matter-option-${m.id}`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 rounded bg-teal-dim text-teal">
                          {m.form_type_label}
                        </span>
                      </div>
                      <p className="font-medium text-white">{m.secondary_label}</p>
                      <p className="text-sm text-slate-500">{m.primary_label} - {m.tertiary_label}</p>
                    </div>
                    <FileText className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {(mode === "standalone" || selectedMatter) && (
          <div className="space-y-8">
            {/* Matter Info */}
            <div className="bg-white rounded-md border p-6" data-testid="matter-info-section">
              <h2 className="text-lg font-semibold text-teal mb-4">Matter Information</h2>
              {selectedMatter && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-blue-700 font-medium">Linked to: </span>
                    <span className="text-blue-600">{formData.matter_title} ({formData.matter_reference})</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedMatter(null); }} className="text-blue-600">
                    Change
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldInput label="Client Name" value={formData.client_name} onChange={(v) => set("client_name", v)} required submitAttempted={submitAttempted} error={validationErrors.client_name} testid="ra-client-name" />
                <FieldInput label="Matter Title" value={formData.matter_title} onChange={(v) => set("matter_title", v)} required submitAttempted={submitAttempted} error={validationErrors.matter_title} testid="ra-matter-title" />
                <FieldInput label="Reference" value={formData.matter_reference} onChange={(v) => set("matter_reference", v)} testid="ra-reference" />
                <FieldInput label="Fee Earner" value={formData.fee_earner} onChange={(v) => set("fee_earner", v)} testid="ra-fee-earner" />
              </div>
            </div>

            {/* 8-factor risk answers */}
            <div className="bg-white rounded-md border p-6" data-testid="risk-factors-section">
              <h2 className="text-lg font-semibold text-teal mb-4">Risk Factor Assessment</h2>
              <p className="text-sm text-slate-500 mb-6">
                Rate each risk factor Low / Medium / High. The overall risk
                band is computed from your answers — you cannot free-pick or
                override the band yourself.
              </p>
              <div className="space-y-3">
                {RISK_FACTORS.map(factor => (
                  <div key={factor.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md border" data-testid={`risk-factor-${factor.id}`}>
                    <div className="flex-1 mr-4">
                      <p className="font-medium text-sm">
                        {factor.label}
                        {factor.weighted && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700">weighted</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400">{factor.description}</p>
                    </div>
                    <div className="flex gap-2">
                      {RISK_CATEGORIES.map(cat => (
                        <button
                          key={cat.value}
                          onClick={() => setRiskFactor(factor.id, cat.value)}
                          className={`px-3 py-1 rounded-md text-xs font-medium border transition-all ${
                            formData.risk_factors[factor.id] === cat.value
                              ? cat.color + " ring-2 ring-offset-1 ring-current"
                              : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"
                          }`}
                          data-testid={`risk-${factor.id}-${cat.value}`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Computed band — READ-ONLY, server-driven. No free-pick override here. */}
              <div className="mt-6 p-4 rounded-md border-2 border-dashed border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-600">Computed Risk Band (read-only):</span>
                    <InfoTooltip id="risk_level" size="sm" />
                  </div>
                  {computedBand ? (
                    <span
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold border ${riskColor(computedBand)}`}
                      data-testid="ra-computed-band"
                    >
                      {computedBand.toUpperCase()}
                      {computedScore !== null && (
                        <span className="ml-2 text-xs font-normal opacity-80">score {computedScore}</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-sm text-slate-400 italic" data-testid="ra-computed-band-pending">
                      Pending answers
                    </span>
                  )}
                </div>
                {computedTriggers.length > 0 && (
                  <ul className="mt-3 text-xs text-slate-500 space-y-0.5">
                    {computedTriggers.map((t, i) => (
                      <li key={i}>• {t}</li>
                    ))}
                  </ul>
                )}
                {submitAttempted && validationErrors.risk_factors && (
                  <p className="mt-2 text-xs text-red-600" data-testid="error-risk-factors">
                    {validationErrors.risk_factors}
                  </p>
                )}
              </div>

              {/* Supervisor-only override block. Hidden for fee earners.
                  Server enforces require_admin too — defence in depth. */}
              {isSupervisor && (
                <div className="mt-4 p-4 rounded-md border border-amber-200 bg-amber-50/40" data-testid="supervisor-override-block">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-amber-900">Supervisor override</p>
                      <p className="text-xs text-amber-800">
                        Override the computed band only with a documented reason
                        (≥{OVERRIDE_REASON_MIN_CHARS} chars). Original computed
                        band is preserved.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOverrideOpen((v) => !v)}
                      data-testid="ra-override-toggle"
                    >
                      {overrideOpen ? "Cancel" : "Override band"}
                    </Button>
                  </div>
                  {overrideOpen && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Override band</Label>
                        <RadioGroup
                          value={overrideBand}
                          onValueChange={setOverrideBand}
                          className="flex gap-3 mt-1"
                          data-testid="ra-override-band"
                        >
                          {["low", "medium", "high"].map((r) => (
                            <label key={r} className="flex items-center gap-1 cursor-pointer">
                              <RadioGroupItem value={r} data-testid={`ra-override-band-${r}`} />
                              <span>{r.charAt(0).toUpperCase() + r.slice(1)}</span>
                            </label>
                          ))}
                        </RadioGroup>
                      </div>
                      <div>
                        <Label>Reason (≥{OVERRIDE_REASON_MIN_CHARS} chars)</Label>
                        <Textarea
                          value={overrideReason}
                          onChange={(e) => setOverrideReason(e.target.value)}
                          rows={3}
                          data-testid="ra-override-reason"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Button onClick={handleOverrideSave} data-testid="ra-override-save">
                          Save override
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Narrative */}
            <div className="bg-white rounded-md border p-6" data-testid="narrative-section">
              <h2 className="text-lg font-semibold text-teal mb-4">Risk Narrative & Mitigations</h2>
              <div className={`border rounded-md p-3 mb-5 flex items-start gap-2 ${narrativeRequired ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
                <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${narrativeRequired ? "text-amber-600" : "text-slate-400"}`} />
                <p className="text-xs">
                  {mitigationsRequired ? (
                    <>
                      <strong>Required for {computedBand} band:</strong> risk
                      narrative and mitigating actions are mandatory (min{" "}
                      {NARRATIVE_MIN_CHARS} chars each).
                    </>
                  ) : computedBand === "Low" ? (
                    <>
                      <strong>Rationale required:</strong> explain why this matter
                      is Low risk (min {NARRATIVE_MIN_CHARS} chars). Mitigating
                      actions become mandatory if the band rises to Medium or High.
                    </>
                  ) : (
                    <>
                      Answer all 8 factors to see whether narrative and
                      mitigations are required for this matter.
                    </>
                  )}
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Material Risks Identified</Label>
                  <Textarea
                    value={formData.material_risks}
                    onChange={e => set("material_risks", e.target.value)}
                    placeholder="Describe the key material risks for this matter..."
                    rows={4}
                    data-testid="ra-material-risks"
                  />
                </div>
                <div>
                  <Label>
                    Risk Narrative {narrativeRequired && <span className="text-red-500">*</span>}
                  </Label>
                  <Textarea
                    value={formData.risk_narrative}
                    onChange={e => set("risk_narrative", e.target.value)}
                    placeholder="Required: explain the risks specific to this matter and why the band is right."
                    rows={4}
                    data-testid="ra-narrative"
                  />
                  {submitAttempted && validationErrors.risk_narrative && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-risk-narrative">
                      {validationErrors.risk_narrative}
                    </p>
                  )}
                </div>
                <div>
                  <Label>
                    Mitigating Actions {mitigationsRequired && <span className="text-red-500">*</span>}
                    {!mitigationsRequired && computedBand === "Low" && (
                      <span className="ml-2 text-xs text-slate-400 font-normal">(optional at LOW band)</span>
                    )}
                  </Label>
                  <Textarea
                    value={formData.mitigating_actions}
                    onChange={e => set("mitigating_actions", e.target.value)}
                    placeholder={mitigationsRequired
                      ? `Required for ${computedBand} band: what steps will be taken.`
                      : "Optional at Low: describe mitigations if useful."}
                    rows={3}
                    data-testid="ra-mitigations"
                  />
                  {submitAttempted && validationErrors.mitigating_actions && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-mitigating-actions">
                      {validationErrors.mitigating_actions}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldInput label="Key Deadline" value={formData.key_deadline} onChange={(v) => set("key_deadline", v)} testid="ra-deadline" placeholder="e.g. Exchange by 31 March 2026" />
                  <div>
                    <Label>Next Review Date</Label>
                    <Input type="date" value={formData.review_date} onChange={e => set("review_date", e.target.value)} data-testid="ra-review-date" />
                  </div>
                </div>
                <div>
                  <Label>Supervisor Notes</Label>
                  <Textarea
                    value={formData.supervisor_notes}
                    onChange={e => set("supervisor_notes", e.target.value)}
                    placeholder="Notes from supervisory review..."
                    rows={2}
                    data-testid="ra-supervisor-notes"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => navigate("/")} data-testid="ra-cancel">
                <ArrowLeft className="w-4 h-4 mr-2" /> Cancel
              </Button>
              <div className="flex items-center gap-3">
                {submitAttempted && hasErrors && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Fix errors above
                  </p>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-teal hover:bg-teal-hover text-white"
                  data-testid="ra-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Risk Assessment
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PageHeader({ navigate, subtitle, rightActions }) {
  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai Law" className="w-10 h-10" />
            <div>
              <h1 className="text-lg font-bold text-teal">Risk Assessment</h1>
              <p className="text-xs text-slate-500">{subtitle}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {rightActions}
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
}

function FieldInput({ label, value, onChange, required, submitAttempted, error, testid, placeholder }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-red-500"> *</span>}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={submitAttempted && error ? "border-red-400 focus:border-red-500" : ""}
        data-testid={testid}
      />
      {submitAttempted && error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}
